import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GET_USERS_QUERY, client } from "@/apis/gql/apolloClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";

import { UserAccess } from "../user-access";
import { UserBasicData } from "../user-basic-data";

import { CompanyRole } from "@/lib/miscellaneous/authorization";
import {
  UserAccountingFirmRole,
  getUserAccountingFirmRole,
} from "@/lib/users/user-roles";
import { useUpdateCompanyUser, useUpdateUser } from "@/queries/users/update";
import type { UserWithAccountingFirmsAndCompanyRoles } from "@/types/user";
import { type FormValues, formSchema } from "../formSchema";

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithAccountingFirmsAndCompanyRoles | null;
}

export function EditUserModal({
  open,
  onOpenChange,
  user,
}: EditUserModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("basicdata");

  const { data: companiesData } = useQuery(USER_COMPANIES_QUERY);

  const [handleUpdateAccountingFirmUser] = useUpdateUser([
    { query: GET_USERS_QUERY, variables: { withCompanyUsers: true } },
    USER_COMPANIES_QUERY,
  ]);
  const [handleUpdateCompanyUser] = useUpdateCompanyUser([
    { query: GET_USERS_QUERY, variables: { withCompanyUsers: true } },
    USER_COMPANIES_QUERY,
  ]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      userType: "accounting_firm",
      name: "",
      email: "",
      accountingFirmRole: UserAccountingFirmRole.USER,
      companyAccess: [],
    },
  });

  useEffect(() => {
    if (!open) {
      setActiveTab("basicdata");
      form.reset();
    }
  }, [open, form]);

  useEffect(() => {
    if (user && open) {
      const { isAccountingFirmUser, prepopulatedCompanyAccess } =
        existingValues();

      form.reset({
        userType: isAccountingFirmUser ? "accounting_firm" : "company",
        name: user.name,
        email: user.email,
        accountingFirmRole: isAccountingFirmUser
          ? (getUserAccountingFirmRole(user) as "user" | "admin")
          : undefined,
        companyAccess: prepopulatedCompanyAccess,
      });
    }
  }, [user, open, form, companiesData]);

  const handleSave = async () => {
    await form.handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const onSubmit = async (data: FormValues) => {
    if (!user) return;

    onOpenChange(false);

    try {
      if (data.userType === "accounting_firm") {
        const accountingFirmId = user.accountingFirms?.[0]?.accountingFirmId;
        const variables = {
          userId: user.userId,
          userData: {
            name: data.name,
            email: data.email,
            accountingFirmRole: data.accountingFirmRole
              ? {
                  accountingFirmId: accountingFirmId,
                  role: data.accountingFirmRole,
                }
              : undefined,
            companyAccess: data.companyAccess,
          },
        };
        await handleUpdateAccountingFirmUser(variables);
      } else {
        const variables = {
          userId: user.userId,
          userData: {
            name: data.name,
            email: data.email,
            companyAccess: data.companyAccess,
          },
        };
        await handleUpdateCompanyUser(variables);
      }
      toast.success(t("users.user_updated_successfully"));
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(t("users.error_updating_user"));
    }
  };

  if (!user) return null;

  const userTypeName = user.accountingFirms?.length
    ? t("users.edit_user_type_accounting_firm")
    : t("users.edit_user_type_company");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] w-[90vw] h-[700px] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{`${userTypeName}`}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <Form {...form}>
            <form className="flex flex-col h-full">
              <div className="flex-1 min-h-0">
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex flex-col h-full"
                >
                  <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
                    <TabsTrigger value="basicdata">
                      {t("users.basic_data")}
                    </TabsTrigger>
                    <TabsTrigger value="accesses">
                      {t("users.accesses")}
                    </TabsTrigger>
                  </TabsList>

                  <div className="mt-6 pr-2 flex-1 overflow-y-auto min-h-0">
                    <TabsContent
                      value="basicdata"
                      className="mt-0 data-[state=inactive]:hidden"
                    >
                      <UserBasicData form={form} />
                    </TabsContent>

                    <TabsContent
                      value="accesses"
                      className="mt-0 data-[state=inactive]:hidden"
                    >
                      <UserAccess form={form} />
                    </TabsContent>
                  </div>
                </Tabs>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t mt-6 flex-shrink-0">
                <Button type="button" variant="outline" onClick={handleCancel}>
                  {t("cancel")}
                </Button>
                <Button type="button" onClick={handleSave}>
                  {t("save")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );

  function existingValues() {
    const isAccountingFirmUser =
      Array.isArray(user?.accountingFirms) && user?.accountingFirms.length;

    let prepopulatedCompanyAccess: { companyId: string; role: CompanyRole }[] =
      [];

    if (isAccountingFirmUser) {
      const companiesWithUser =
        companiesData?.userCompanies?.filter(
          (company: { companyId: string; users?: { userId: string }[] }) =>
            company.users?.some(
              (companyUser) => companyUser.userId === user.userId,
            ),
        ) || [];
      prepopulatedCompanyAccess = companiesWithUser.map(
        (company: { companyId: string }) => ({
          companyId: company.companyId,
          role: CompanyRole.EDITOR,
        }),
      );
    } else {
      const roles =
        (user as UserWithAccountingFirmsAndCompanyRoles).companies || [];

      prepopulatedCompanyAccess = roles.map((r) => ({
        companyId: r.companyId,
        role:
          r.role === CompanyRole.CLIENT_SELF_BOOKING
            ? CompanyRole.CLIENT_SELF_BOOKING
            : CompanyRole.CLIENT,
      }));
    }
    return { isAccountingFirmUser, prepopulatedCompanyAccess };
  }
}
